import { validateSchema, type ValidationResult } from "@jasper/forms";
import { z } from "zod";

import type { ConfigurableProduct, ProductStepProps } from "../types";
import {
  adjacentTeethSchema,
  ItemScopeEnum,
  type ItemType,
} from "@jasper/client-models";
import {
  GenericTeethStep,
  type TeethStepValidatorInput,
} from "./GenericTeethStep";
import type { UpdateProductArgs } from "@jasper/use-cases";
import { useTranslation } from "react-i18next";
import { formatTeeth } from "../../hooks/useProductDescription/teeth";
import { useProductTranslations } from "@jasper/translations";

type Props = ProductStepProps & {
  applyTeethSelection?: (args: {
    teeth: number[];
    product: ConfigurableProduct;
    itemType: ItemType;
  }) => Promise<UpdateProductArgs> | UpdateProductArgs;
};

const validateSelection = ({
  value,
  setTeethInError,
}: TeethStepValidatorInput): Record<string, unknown> | undefined => {
  setTeethInError([]);

  const flattenedTeeth = value.groupedTeeth.flat();
  if (
    flattenedTeeth.length !== value.teeth.length ||
    flattenedTeeth.some(tooth => !value.teeth.includes(tooth))
  ) {
    return {
      teeth: "unexpected",
    };
  }

  // In that case all groups only contains one teeth, so we can validate the whole selection at once to fallback on default error messages.
  if (value.groupedTeeth.length === value.teeth.length) {
    const result = validateSchema(
      z.object({
        teeth: adjacentTeethSchema,
      }),
      { teeth: flattenedTeeth }
    );

    if (!result.success) {
      return result.errors;
    }
  }

  const result = value.groupedTeeth.reduce<
    ValidationResult<{ teeth: number[] }>
  >(
    (acc, group) => {
      const result = validateSchema(
        z.object({
          teeth: adjacentTeethSchema,
        }),
        { teeth: group }
      );

      if (!result.success) {
        setTeethInError(prev => [...prev, ...group]);
        return {
          success: false,
          errors: {
            ...("errors" in acc ? acc.errors : {}),
            ...result.errors,
          },
        };
      }

      return acc;
    },
    { success: true, data: value }
  );

  return result.success ? undefined : result.errors;
};

const defaultApplyTeethSelection = ({
  teeth,
  product,
  itemType,
}: {
  teeth: number[];
  product: ConfigurableProduct;
  itemType: ItemType;
}): UpdateProductArgs => {
  const itemsToSave = teeth.map(tooth => {
    const existingItem =
      product.items.find(
        item => item.scope === ItemScopeEnum.tooth && item.tooth === tooth
      ) ?? product.items[0];

    return {
      tooth,
      type: itemType,
      materialCharacteristics: existingItem?.materialCharacteristics,
      inlayCoreCharacteristics: existingItem?.inlayCoreCharacteristics,
    };
  });

  return {
    productId: product.id,
    teeth: teeth,
    items: itemsToSave,
  };
};

export const AdjacentTeethStep = ({
  applyTeethSelection = defaultApplyTeethSelection,
  ...props
}: Props) => {
  const { t } = useTranslation("product-workflows", {
    keyPrefix: "steps.teeth",
  });
  const { t: translateProduct } = useProductTranslations();

  return (
    <GenericTeethStep
      {...props}
      validateSelection={validateSelection}
      groupAdjacentByDefault
      applyTeethSelection={applyTeethSelection}
      validateOnChange
      solidarizedLabel={translateProduct(props.products[0].type, {
        count: props.products.length,
      })}
      getTeethDescription={({ teeth, groupedTeeth = [], descriptionLevel }) => {
        if (!teeth.length) {
          return {
            badgeContent: "-",
            description: t("noToothSelected"),
          };
        }

        return {
          badgeContent: formatTeeth(teeth, t),
          description:
            descriptionLevel === "global"
              ? `${groupedTeeth.length} ${translateProduct(props.products[0].type, { count: groupedTeeth.length })} · ${teeth.length} ${t("labels.element", { count: teeth.length, keyPrefix: "steps.adjacentTeeth" })}`
              : `${teeth.length} ${t("labels.element", { count: teeth.length, keyPrefix: "steps.adjacentTeeth" })}`,
        };
      }}
      hideGroupAllButtons
    />
  );
};
